<template>
  <div>
    <b-form-group label="Transformation type">
      <Multiselect
        v-model="selectedTransformationObj"
        :options="filteredTransformationOptions"
        label="text"
        track-by="value"
      />
    </b-form-group>

    <div v-if="disableForEmptyAvailable.includes(selectedTransformation)">
      <b-form-group
        v-if="selectedTransformation"
      >
        <b-form-checkbox
          v-model="disableForEmpty"
        >
          Disable for empty value
        </b-form-checkbox>
      </b-form-group>

      <b-form-group
        v-if="selectedTransformation && !disableForEmpty"
        label="Fallback text"
      >
        <b-form-input
          v-model="emptyText"
          type="text"
          placeholder="Text if value is empty"
        />
      </b-form-group>
    </div>

    <b-form-group
      v-if="ignoreHtmlTagsIsVisible"
    >
      <b-form-checkbox
        v-model="ignoreLineBreaks"
      >
        Also ignore Line Breaks
      </b-form-checkbox>
    </b-form-group>

    <b-form-group
      v-if="selectedTransformation === 'split-field-by-space'"
      label="Select fields to split by space"
    >
      <Multiselect
        v-model="splitBySpaceFields"
        :options="templateFields"
        :multiple="true"
      />
    </b-form-group>

    <b-form-group
      v-if="cropToImageIsVisible"
      label="Percentage"
    >
      <b-form-input
        v-model="cropPercentage"
        type="number"
        min="0"
        max="100"
        step="1"
        placeholder="Crop percentage"
      />
    </b-form-group>

    <div v-if="rotateToWiderSideIsVisible">
      <b-form-group label="Rotate image only if field matches">
        <b-form-select
          v-model="rotateConditionField"
          :options="rotateConditionFieldOptions"
        />
      </b-form-group>

      <b-form-group
        v-if="rotateConditionField"
        label="Matching value"
      >
        <b-form-input
          v-model="rotateConditionValue"
          type="text"
          placeholder="Value"
        />
      </b-form-group>
    </div>

    <b-form-group
      v-if="selectedTransformation === 'download-image-from-box'"
      label="Box folder ID"
      description="Leave empty to search the Box root folder."
    >
      <b-form-input
        v-model="boxFolderId"
        type="text"
        placeholder="Box folder ID"
      />
    </b-form-group>

    <div v-if="selectedTransformation === 'columns-concatenation' || selectedTransformation === 'concatenate'">
      <b-form-group label="Concatenation sequence">
        <div
          v-if="!concatenationSequence.length"
          class="text-muted small mb-2"
        >
          Add fields or strings to build the sequence.
        </div>

        <div
          v-for="(item, index) in concatenationSequence"
          :key="`concatenation-item-${index}`"
          class="d-flex align-items-center mb-2"
        >
          <span class="mr-2">{{ index + 1 }}.</span>

          <b-form-select
            v-if="item.type === 'field'"
            v-model="item.value"
            :options="concatenationFieldOptions"
            class="mr-2"
          />

          <b-form-input
            v-else
            v-model="item.value"
            type="text"
            placeholder="Text to insert (new line: \n or \newline)"
            class="mr-2"
          />

          <b-button
            size="sm"
            variant="outline-danger"
            @click="removeConcatenationItem(index)"
          >
            Remove
          </b-button>
        </div>

        <div class="d-flex flex-wrap align-items-end">
          <div class="mr-2 mb-2">
            <b-form-select
              v-model="concatenationFieldToAdd"
              :options="concatenationFieldOptions"
              style="min-width: 200px;"
            />
          </div>

          <b-button
            class="mr-2 mb-2"
            variant="primary"
            @click="addConcatenationField"
          >
            Add field
          </b-button>

          <b-button
            class="mb-2"
            variant="secondary"
            @click="addConcatenationString"
          >
            Add string
          </b-button>
        </div>
      </b-form-group>
    </div>
    <div v-if="selectedTransformation === 'values-to-header'">
      <b-form-group label="Map value to header">
        <div
          class="text-muted small mb-2"
        >
          Select the field that will be mapped to the header (use %s as placeholder on the header name)
          <b-form-select
            v-model="valueFieldToAdd"
            :options="concatenationFieldOptions"
            style="min-width: 200px;"
          />
        </div>
      </b-form-group>
    </div>
    <b-form-group
      v-if="selectedTransformation === 'columns-multiplier' || selectedTransformation === 'multiplier'"
      label="Select columns to multiply"
    >
      <Multiselect
        v-model="headersToMultiply"
        :options="xlsxHeaders"
        :multiple="true"
      />
    </b-form-group>

    <div v-if="selectedTransformation === 'format'">
      <b-form-group label="Format field value">
        <b-form-select
          v-model="formatField"
          :options="formatOptions"
        />
      </b-form-group>

      <b-form-group
        v-if="formatField === 'thousand_separator'"
        label="Thousand separator symbol"
      >
        <b-form-input
          v-model="thousandSeparator"
          type="text"
          placeholder="Thousand separator (e.g., , or .)"
          maxlength="1"
        />
      </b-form-group>
    </div>

    <!-- old transformation-modal--form-shorten-product-description -->
    <b-form-group
      v-if="productDescriptionIsVisible"
      label="Output words"
    >
      <b-form-input
        v-model="outputWords"
        type="number"
        placeholder="Output words"
      />
    </b-form-group>

    <b-form-group
      v-if="htmlListToCommaSeparatedValuesIsVisible"
      label="Delimiter character"
    >
      <b-form-input
        v-model="htmlListDelimiter"
        type="text"
        placeholder="Delimiter character (default: ,)"
      />
    </b-form-group>
    
    <b-row
      v-if="htmlListToCommaSeparatedValuesIsVisible"
    >
      <b-col cols="8">
        <b-form-checkbox
          v-model="maximumNumberOfValuesEnabled"
        >
          Set maximum number of values to use
        </b-form-checkbox>
      </b-col>
      <b-col cols="4">
        <b-form-input
          v-if="maximumNumberOfValuesEnabled"
          v-model="maximumNumberOfValues"
          type="number"
          placeholder="Maximum number of values"
        />
      </b-col>
    </b-row>

    <!-- old transformation-modal--form-select-separator -->
    <b-form-group
      v-if="formSelectSeparatorIsVisible"
      label="Delimiter character"
    >
      <b-form-input
        v-model="delimiterCharacter"
        type="text"
        placeholder="Delimiter character"
      />
    </b-form-group>


    <!-- add-line-break-after-character -->
    <b-form-group
      v-if="formSelectCharacterIsVisible"
    >
      <b-form-checkbox
        v-model="addLineBreakIfWidthExceeded"
      >
        Only if row width is exceeded
      </b-form-checkbox>
    </b-form-group>
    
    <b-form-group
      v-if="formSelectCharacterIsVisible"
      label="Character"
    >
      <b-form-input
        v-model="lineBreakCharacter"
        type="text"
        placeholder="Enter the character"
      />
    </b-form-group>

    <!-- map-field-based-on-characters-count -->
    <b-form-group
      v-if="formMapFieldBasedOnCharsCountIsVisible"
      label="Group ID"
    >
      <b-form-input
        v-model="mapFieldBasedOnCharsCountGroupID"
        type="number"
      />
    </b-form-group>

    <b-form-group
      v-if="formSplitToRowsIsVisible"
      label="Columns count"
    >
      <b-form-input
        v-model="splitRowsColsCount"
        type="number"
        min="2"
      />
    </b-form-group>
    <b-form-group
      v-if="formSplitToRowsIsVisible"
    >
      <b-form-checkbox
        v-model="splitRowsAlignBulletsHorizontally"
      >
        Align bullets horizontally
      </b-form-checkbox>
    </b-form-group>
    <b-form-group
      v-if="formSplitToRowsIsVisible"
      label="Sort bullets by number of characters"
    >
      <b-form-select
        v-model="splitRowsSortByNumberOfCharacters"
        :options="[
          { value: false, text: 'Off' },
          { value: 'asc', text: 'Ascending' },
          { value: 'desc', text: 'Descending' }
        ]"
      />
    </b-form-group>
    <b-form-group
      v-if="formSplitToRowsIsVisible"
      label="Move bullets to next column if column height is exceeded"
    >
      <b-form-input
        v-model="splitRowsMoveBulletsToNextColumnIfHeightExceeded"
        type="number"
        min="0"
        step="1"
        placeholder="Column height limit (px)"
      />
    </b-form-group>

    <!-- old transformation-modal--form-select-substring -->
    <b-form-group
      v-if="formSelectSubstringIsVisible"
      label="Text to add"
    >
      <b-form-input
        v-model="substring"
        type="text"
        placeholder="Enter the character/string to append/prepend"
      />
    </b-form-group>

    <!-- old transformation-modal--form-select-configurable-bullets -->
    <div v-if="formSelectConfigurableBulletsIsVisible">
      <b-form-group>
        <b-form-select
          v-model="bulletsToAdd"
          :options="bulletsOptions"
        />
      </b-form-group>

      <b-form-group v-if="addLineJumpIsVisible">
        <b-form-checkbox
          v-model="addLineJump"
          class="ml-3"
        >
          Add line jump
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox
          v-model="addSubsequentIndentation"
          class="ml-3"
        >
          Add subsequent lines indentation
        </b-form-checkbox>
      </b-form-group>

      <b-form-group
        v-if="itemsMarginIsVisible"
        label="Margin between list items"
      >
        <b-form-input
          v-model="itemsMargin"
          type="number"
          min="0"
        />
      </b-form-group>

      <b-form-group v-if="sortValuesByCharacterCount">
        <b-form-checkbox
          v-model="sortValuesByCharacterCountParam"
          class="ml-3"
        >
          Sort values based on character count
        </b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="sortValuesByCharacterCount && sortValuesByCharacterCountParam">
        <b-form-select
          v-model="sortValuesByCharacterCountDirection"
          :options="sortDirectionOptions"
          class="ml-3"
          style="max-width: 200px;"
        />
      </b-form-group>

      <b-form-group
        v-if="customTextIsVisible"
        label="Custom text:"
      >
        <b-form-input
          v-model="bulletsCustom"
          type="text"
        />
      </b-form-group>

      <b-form-group label="Bullet Color (#000001 to use text color):">
        <b-form-input
          v-model="bulletColor"
          type="color"
          placeholder="Bullet color"
        />
      </b-form-group>
    </div>

    <!-- old transformation-modal--form-custom-script -->
    <div v-show="formCustomScriptIsVisible">
      <b-form-group>
        <b-form-select
          v-model="transformationScript"
          :options="savedScriptsOptions"
        />
      </b-form-group>

      <b-form-group label="Custom script">
        <b-form-textarea
          v-if="!codeEditor"
          :value="customScript"
          rows="10"
        />

        <div
          ref="customScriptCodeEditor"
          class="custom-script-code-editor"
        />
      </b-form-group>

      <div>
        <b-button
          variant="primary"
          @click="testScript"
        >
          Test script
        </b-button>

        <b-button
          variant="primary"
          @click="saveScript"
        >
          Save script
        </b-button>

        <b-button
          v-if="transformationScript"
          variant="danger"
          @click="deleteScript"
        >
          Delete script
        </b-button>
      </div>

      <div
        v-show="customScriptOutputVisible"
        class="card custom-script-output"
      >
        <div class="card-header">
          Output:
        </div>

        <div
          ref="customScriptOutput"
          class="card-body"
          style="white-space: pre-wrap;"
        />
      </div>
    </div>

    <div
      v-if="replaceEmptyValuesIsVisible"
    >
      <b-form-group label="Replacement">
        <b-form-input
          v-model="replaceEmptyValuesText"
          type="text"
          placeholder="Replacement text"
        />
      </b-form-group>
      <b-form-group label="Field">
        <b-form-select
          v-model="replaceEmptyValuesField"
          :options="xlsxHeaders"
        />
      </b-form-group>
    </div>

    <div
      v-if="replaceFractionsWithGlyphIsVisible"
    >
      <b-form-group label="Field">
        <b-form-select
          v-model="replaceFractionsWithGlyphField"
          :options="xlsxHeaders"
        />
      </b-form-group>
    </div>
    
    <div
      v-if="emptyWhenRelatedFieldIsEmptyIsVisible"
    >
      <b-form-group
        label="Field"
      >
        <b-form-select
          v-model="emptyRelatedFieldName"
          :options="templateFields"
          :multiple="false"
        />
      </b-form-group>
    </div>

    <b-card
      v-if="conditionalValuesIsVisible"
      class="mb-3"
      body-class="p-3"
    >
      <b-row class="align-items-center mb-3">
        <b-col cols="auto">
          <span>If: </span>
        </b-col>

        <b-col class="conditional-values-if-col">
          <Multiselect
            v-if="conditionalValuesUsesTableColumns"
            v-model="selectedTableColumnObj"
            :options="tableColumnOptions"
            label="text"
            track-by="value"
          />
          <Multiselect
            v-else
            v-model="selectedColumn"
            :options="xlsxHeaders"
            :multiple="false"
          />
        </b-col>

        <b-col cols="auto">
          <b-form-select
            v-model="selectedCondition"
            :options="[
              { value: '=', text: 'Equals' },
              { value: '!=', text: 'Does not equal' },
              { value: 'empty', text: 'Is Empty' },
              { value: 'not_empty', text: 'Not Empty' }
            ]"
          />
        </b-col>

        <template v-if="!conditionIsEmptiness">
          <b-col cols="auto">
            To:
          </b-col>

          <b-col>
            <b-form-input
              v-model="conditionValue"
              type="text"
              placeholder="Value"
            />
          </b-col>
        </template>
      </b-row>

      <b-row class="align-items-center">
        <b-col cols="auto">
          <span>Then change it to: </span>
        </b-col>

        <b-col>
          <b-form-input
            v-model="newValue"
            type="text"
            placeholder="New value"
          />
        </b-col>
      </b-row>
    </b-card>
    <div v-if="renameHeadersIsVisible">
      <div
        v-if="renameHeadersHeaders.length"
        class="table-responsive"
      >
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>Headers</th>
              <th>New header name</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="header in renameHeadersHeaders"
              :key="header"
            >
              <td class="align-middle">
                {{ header }}
              </td>
              <td>
                <b-form-input
                  :value="headerRenames[header] || ''"
                  type="text"
                  placeholder="Keep original"
                  @input="setHeaderRename(header, $event)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-muted small"
      >
        No headers available.
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { EditorState } from '@codemirror/state';
import { basicSetup } from "codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { EditorView } from "@codemirror/view";
import { transformationsEventBroker, TRANSFORMATIONS_EVENTS } from '@frontend/components/modules/edit-page/import/transformations/event-broker';

export default {
  name: 'TransformationForm',
  components: {
    Multiselect,
  },
  props: {
    transformation: {
      type: Object,
      required: false,
      default: () => ({
        trasnformation: null,
        varriables: {}
      }),
    },
    scripts: {
      type: Array,
      required: false,
      default: () => [],
    },
    xlsxHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },
    tableHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },
    templateFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    context: {
      type: String,
      required: false,
      default: 'field', // 'field' for field values, 'table' for table columns
    },
    tableColumnIndex: {
      type: [Number, String],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      selectedTransformation: null,
      transformationOptions: [
        /* Please keep these ordered alphabetically !!! */
        { value: null, text: 'No transformation' },
        { value: 'add-line-break-after-character', text: 'Add a line break after the character' },
        { value: 'add-subsequent-line-indentation', text: 'Add subsequent lines indentation' },        
        { value: 'append', text: 'Append character/text' },
        { value: 'columns-multiplier', text: 'Columns multiplier' },
        { value: 'columns-concatenation', text: 'Concatenate Fields' },
        { value: 'concatenate', text: 'Concatenate fields' },
        { value: 'conditional-values', text: 'Conditional values' },
        { value: 'custom-script', text: 'Custom script' },
        { value: 'crop-to-image', text: 'Crop to image' },
        { value: 'download-image-from-box', text: 'Download image from Box' },
        { value: 'excel_date_yyyy_mm_dd', text: 'Excel date to yyyy-mm-dd' },
        { value: 'excel_date_dd_mm_yyyy', text: 'Excel date to dd/mm/yyyy' },
        { value: 'format', text: 'Format field' },
        { value: 'generate-QR', text: 'Generate QR code' },
        { value: 'html-list-to-comma-separated-values', text: 'HTML list to comma separated values' },        
        { value: 'html-text', text: 'HTML text with <strong> or <bold>' },
        { value: 'ignore-html-tags', text: 'Ignore HTML tags' },
        { value: 'empty-when-related-field-is-empty', text: 'Make empty when related field is empty' },
        { value: 'empty-when-fields-related-to-this-are-empty', text: 'Make empty when fields related to this are empty' },
        { value: 'map-field-based-on-characters-count', text: 'Map text field based on character count' },        
        /* Please keep these ordered alphabetically !!! */
        { value: 'values-to-header', text: 'Map values to header' },
        { value: 'multiplier', text: 'Columns multiplier' },
        { value: 'multiple-columns-to-icons', text: 'Multiple columns to icons' },
        { value: 'multiple-images', text: 'Multiple images' },
        { value: 'values-to-bullets', text: 'Multiple values to bullets' },
        { value: 'values-to-configurable-bullets', text: 'Multiple values to configurable bullets' },
        { value: 'bullet-to-text', text: 'Prepend bullet to text' },
        { value: 'prepend', text: 'Prepend character/text' },
        { value: 'reduce-product-name', text: 'Reduce product name' },
        { value: 'remove-background-from-image', text: 'Remove background from image' },        
        { value: 'remove-special-characters', text: 'Remove special characters' },
        { value: 'replace-product-table-empty-values', text: 'Replace "Product Table" empty values' },     
        { value: 'replace-product-table-fractions-with-glyph', text: 'Replace "Product Table" Fractions with Glyph' },           
        { value: 'rotate-to-wider-side', text: 'Rotate image to wider side' },
        { value: 'shorten-product-description', text: 'Shorten product description' },
        { value: 'shorten-term-description', text: 'Shorten term description' },
        { value: 'split-field-by-space', text: 'Split field by space' },
        { value: 'split-to-rows', text: 'Split bullets to multiple columns' },
        { value: 'combine-adjacent-cells', text: 'Table: combine adjacent cells with equal values' },
        { value: 'combine-adjacent-headers', text: 'Table: combine adjacent headers with equal values' },
        { value: 'merge-table-cells-with-same-value', text: 'Table: Merge cells with same value' },
        { value: 'rename-headers', text: 'Table: Rename Headers' },
        { value: 'static-table', text: 'Table with static number of rows' },
        { value: 'truncate', text: 'Truncate' },
        /* Please keep these ordered alphabetically !!! */
      ],
      bulletsOptions: [
        { value: null, text: 'Select bullet' },
        { value: 'custom_text', text: 'Custom text' },
        {
          label: '● Circular Shapes',
          options: [
            { value: '●', text: '● Solid Circle' },
            { value: '○', text: '○ Hollow Circle' },
            { value: '◉', text: '◉ Target Circle' },
            { value: '◎', text: '◎ Double Circle' },
            { value: '◌', text: '◌ Dotted Circle' },
          ],
        },
        {
          label: '■ Square & Rectangular Shapes',
          options: [
            { value: '■', text: '■ Solid Square' },
            { value: '□', text: '□ Hollow Square' },
            { value: '▪', text: '▪ Small Solid Square' },
            { value: '▫', text: '▫ Small Hollow Square' },
            { value: '▮', text: '▮ Block Square' },
            { value: '▯', text: '▯ Outline Square' },
          ],
        },
        {
          label: '▶ Triangular Shapes',
          options: [
            { value: '▶', text: '▶ Black Right Triangle' },
            { value: '▷', text: '▷ White Right Triangle' },
            { value: '◀', text: '◀ Black Left Triangle' },
            { value: '◁', text: '◁ White Left Triangle' },
            { value: '▲', text: '▲ Black Up Triangle' },
            { value: '△', text: '△ White Up Triangle' },
            { value: '▼', text: '▼ Black Down Triangle' },
            { value: '▽', text: '▽ White Down Triangle' },
            { value: '◢', text: '◢ Lower Right Triangle' },
            { value: '◣', text: '◣ Lower Left Triangle' },
            { value: '◤', text: '◤ Upper Left Triangle' },
            { value: '◥', text: '◥ Upper Right Triangle' },
          ],
        },
        {
          label: '★ Star Shapes',
          options: [
            { value: '★', text: '★ Black Star' },
            { value: '☆', text: '☆ White Star' },
          ]
        },
        {
          label: '➤ Arrow Shapes',
          options: [
            { value: '➤', text: '➤ Black Arrowhead' },
            { value: '➜', text: '➜ Medium Arrow' },
            { value: '➝', text: '➝ Thin Arrow' },
            { value: '➡', text: '➡ Right Arrow' },
            { value: '⬅', text: '⬅ Left Arrow' },
            { value: '⬆', text: '⬆ Up Arrow' },
            { value: '⬇', text: '⬇ Down Arrow' },
            { value: '↗', text: '↗ Diagonal Up-Right' },
            { value: '↘', text: '↘ Diagonal Down-Right' },
            { value: '↙', text: '↙ Diagonal Down-Left' },
            { value: '↖', text: '↖ Diagonal Up-Left' },
          ],
        },
        {
          label: '♦ Diamond Shapes',
          options: [
            { value: '♦', text: '♦ Black Diamond' },
            { value: '◇', text: '◇ White Diamond' },
          ],
        },
      ],
      outputWords: 2,
      maximumNumberOfValues: 10,
      maximumNumberOfValuesEnabled: false,
      htmlListDelimiter: ',',
      delimiterCharacter: null,
      substring: null,
      ignoreLineBreaks: true,

      bulletsToAdd: null,
      addLineJump: 0,
      sortValuesByCharacterCountParam: 0,
      sortValuesByCharacterCountDirection: 'desc',
      addSubsequentIndentation: 0,
      bulletsCustom: '',
      bulletColor: '#000001',
      
      mapFieldBasedOnCharsCountGroupID: 1,

      transformationScript: null,
      customScript: null,
      codeEditor: null,
      customScriptOutputVisible: false,
      lineBreakCharacter: null,
      addLineBreakIfWidthExceeded: false,

      headersToMultiply: [],
      cropPercentage: 10,
      boxFolderId: '',
      
      splitBySpaceFields: [],
      splitRowsColsCount: 2,
      itemsMargin: 0,

      concatenationSequence: [],
      concatenationFieldToAdd: null,
      valueFieldToAdd: null,

      replaceEmptyValuesText: null,
      replaceEmptyValuesField: null,

      replaceFractionsWithGlyphField: null,
      emptyRelatedFieldName: null,
      rotateConditionField: null,
      rotateConditionValue: '',

      formatField: null,
      thousandSeparator: ',',

      disableForEmpty: true,
      emptyText: '',

      headerRenames: {},
      splitRowsAlignBulletsHorizontally: false,
      splitRowsSortByNumberOfCharacters: false,//false|asc|desc
      splitRowsMoveBulletsToNextColumnIfHeightExceeded: null,
      
      selectedColumn: null,
      selectedTableColumn: 'all',
      selectedCondition: '=',
      conditionValue: '',
      newValue: ''
    }
  },
  computed: {
    formatOptions() {
      return [
        { value: null, text: 'Select format' },
        { value: 'price_format', text: 'Price format (12,345.00)' },
        { value: 'capitalize', text: 'Capitalize' },
        { value: 'lowercase', text: 'Lowercase' },
        { value: 'uppercase', text: 'Uppercase' },
        { value: 'trim', text: 'Trim whitespaces' },
        { value: 'thousand_separator', text: 'Thousand separator' },
        { value: 'remove_trailing_zeros', text: 'Remove trailing zeros in decimals' },
      ];
    },
    concatenationFieldOptions() {
      const options = [{ value: null, text: 'Select column...' }];
      this.xlsxHeaders.forEach((header) => options.push({ value: header, text: header }));
      return options;
    },
    rotateConditionFieldOptions() {
      return [
        { value: null, text: 'Always rotate' },
        ...this.xlsxHeaders.map((header) => ({ value: header, text: header })),
      ];
    },
    // Table-specific transformations (only these should be shown for table columns)
    tableTransformations() {
      return ['prepend', 'append', 'truncate', 'format', 'multiplier', 'concatenate', 'values-to-header', 'ignore-html-tags', 'merge-table-cells-with-same-value'];
    },
    disableForEmptyAvailable() {
      return this.tableTransformations;
    },
    // Filter transformation options based on context
    filteredTransformationOptions() {
      if (this.context === 'table') {
        // For table columns, only show table-specific transformations
        return this.transformationOptions.filter(option => 
          !option.value || this.tableTransformations.includes(option.value)
        );
      } else {
        // For field values, exclude table-specific transformations (multiplier, concatenate, format)
        // but keep prepend, append, truncate as they're used for both
        return this.transformationOptions.filter(option => 
          !option.value || !['multiplier', 'concatenate', 'format'].includes(option.value)
        );
      }
    },
    selectedTransformationObj: {
      get () {
        return _.find(this.filteredTransformationOptions, ['value', this.selectedTransformation])
      },
      set (transformation) {
        this.selectedTransformation = transformation?.value
      }
    },
    savedScriptsOptions() {
      return [
        { value: null, text: 'Select a saved custom script...' },
        ...this.scripts.map((script) => ({value: script.id, text: script.name})),
      ];
    },
    sortDirectionOptions() {
      return [
        { value: 'asc', text: 'Ascending' },
        { value: 'desc', text: 'Descending' },
      ];
    },
    productDescriptionIsVisible() {
      return this.selectedTransformation === 'shorten-product-description'
    },
    htmlListToCommaSeparatedValuesIsVisible() {
      return this.selectedTransformation === 'html-list-to-comma-separated-values'
    },
    ignoreHtmlTagsIsVisible() {
      return this.selectedTransformation === 'ignore-html-tags'
    },
    formSelectSeparatorIsVisible() {
      return ['values-to-bullets', 'values-to-configurable-bullets', 'multiple-images', 'truncate'].includes(this.selectedTransformation);
    },
    formSelectCharacterIsVisible() {
      return this.selectedTransformation === 'add-line-break-after-character';
    },    
    cropToImageIsVisible() {
      return this.selectedTransformation === 'crop-to-image';
    },
    rotateToWiderSideIsVisible() {
      return this.selectedTransformation === 'rotate-to-wider-side';
    },
    formMapFieldBasedOnCharsCountIsVisible() {
      return this.selectedTransformation === 'map-field-based-on-characters-count';
    },
    formSplitToRowsIsVisible() {
      return this.selectedTransformation === 'split-to-rows';
    },
    formSelectSubstringIsVisible() {
      return ['append', 'prepend'].includes(this.selectedTransformation);
    },
    formSelectConfigurableBulletsIsVisible() {
      return ['values-to-configurable-bullets', 'bullet-to-text'].includes(this.selectedTransformation);
    },
    addLineJumpIsVisible() {
      return ['values-to-configurable-bullets'].includes(this.selectedTransformation);
    },
    sortValuesByCharacterCount() {
      return ['values-to-configurable-bullets'].includes(this.selectedTransformation);
    },
    customTextIsVisible() {
      return this.bulletsToAdd === 'custom_text';
    },
    itemsMarginIsVisible() {
      return this.selectedTransformation === 'values-to-configurable-bullets' && !!this.addSubsequentIndentation;
    },
    formCustomScriptIsVisible() {
      if (this.context === 'table') {
        return false;
      }
      return ['custom-script', 'multiple-images', 'truncate'].includes(this.selectedTransformation);
    },
    emptyWhenRelatedFieldIsEmptyIsVisible() {
      return this.selectedTransformation === 'empty-when-related-field-is-empty';
    },
    conditionalValuesIsVisible() {
      return this.selectedTransformation === 'conditional-values';
    },
    conditionalValuesUsesTableColumns() {
      return this.context === 'field' && this.tableHeaders.length > 0;
    },
    tableColumnOptions() {
      return [
        { value: 'all', text: 'All columns' },
        ...this.tableHeaders.map((header, index) => ({ value: index, text: header })),
      ];
    },
    selectedTableColumnObj: {
      get() {
        return _.find(this.tableColumnOptions, ['value', this.selectedTableColumn]);
      },
      set(option) {
        this.selectedTableColumn = option ? option.value : 'all';
      },
    },
    conditionIsEmptiness() {
      return ['empty', 'not_empty'].includes(this.selectedCondition);
    },
    replaceEmptyValuesIsVisible() {
      return this.selectedTransformation === 'replace-product-table-empty-values';
    },
    replaceFractionsWithGlyphIsVisible() {
      return this.selectedTransformation === 'replace-product-table-fractions-with-glyph';
    },
    renameHeadersIsVisible() {
      return this.selectedTransformation === 'rename-headers';
    },
    renameHeadersHeaders() {
      const source = (this.tableHeaders && this.tableHeaders.length)
        ? this.tableHeaders
        : (this.xlsxHeaders && this.xlsxHeaders.length)
          ? this.xlsxHeaders
          : Object.keys(this.headerRenames || {});

      const headers = (source || [])
        .map((header) => (header === null || header === undefined ? '' : String(header)))
        .map((header) => header.trim())
        .filter((header) => header !== '');
      return Array.from(new Set(headers));
    },
  },
  watch: {
    transformationScript: {
      handler(newVal) {
        this.customScript = newVal
          ? this.scripts.find((script) => script.id === newVal).content
          : null;

        this.initializeCodeMirror();
        this.hideTestOutput();
      },
    },
    selectedTransformation: {
      handler(newVal) {
        if (this.formCustomScriptIsVisible) {
          this.initializeCodeMirror();
        }
        if (newVal === 'rename-headers') {
          this.initializeHeaderRenames();
        }
      },
    },
  },
  mounted() {
    if (!this.transformation.transformation) {
      return;
    }

    this.selectedTransformation = this.transformation.transformation;

    if (['shorten-product-description'].includes(this.selectedTransformation)) {
      this.outputWords = this.transformation.variables.outputWords;
    }

    if (['html-list-to-comma-separated-values'].includes(this.selectedTransformation)) {
      this.maximumNumberOfValuesEnabled = this.transformation.variables.maximumNumberOfValuesEnabled;
      this.maximumNumberOfValues = this.maximumNumberOfValuesEnabled ? this.transformation.variables.maximumNumberOfValues : 10;
      this.htmlListDelimiter = this.transformation.variables.delimiter || ',';
    }

    if (['ignore-html-tags'].includes(this.selectedTransformation)) {
      this.ignoreLineBreaks = !!this.transformation.variables.ignoreLineBreaks;
    }

    if (['crop-to-image'].includes(this.selectedTransformation)) {
      this.cropPercentage = this.normalizeCropPercentage(
        this.transformation.variables.cropPercentage ?? this.transformation.variables.crop_percentage ?? 10
      );
    }

    if (['download-image-from-box'].includes(this.selectedTransformation)) {
      this.boxFolderId = this.transformation.variables.folderId || '';
    }

    if (['rotate-to-wider-side'].includes(this.selectedTransformation)) {
      this.rotateConditionField = this.transformation.variables.rotateConditionField || null;
      this.rotateConditionValue = this.transformation.variables.rotateConditionValue ?? '';
    }

    if (['values-to-bullets', 'multiple-images'].includes(this.selectedTransformation)) {
      this.delimiterCharacter = this.transformation.variables.delimiter;
    }

    if (['truncate'].includes(this.selectedTransformation)) {
      this.delimiterCharacter = this.transformation.variables.delimiter || this.transformation.variables.truncate;
    }

    if (['values-to-configurable-bullets'].includes(this.selectedTransformation)) {
      this.delimiterCharacter = this.transformation.variables.delimiter;

      this.setBullets();
      this.addLineJump = !!this.transformation.variables.add_line_jump;
      this.addSubsequentIndentation = !!this.transformation.variables.add_subsequent_lines_indentation;
      this.sortValuesByCharacterCountParam = !!this.transformation.variables.sort_values_by_character_count;
      // Default to 'desc' for new transformations, or if direction is missing (backward compatibility)
      this.sortValuesByCharacterCountDirection = this.transformation.variables.sort_values_by_character_count_direction || 'desc';
      this.itemsMargin = this.transformation.variables.items_margin || 0;
    }

    if (['bullet-to-text'].includes(this.selectedTransformation)) {
      this.setBullets();
      this.addSubsequentIndentation = !!this.transformation.variables.add_subsequent_lines_indentation;
    }

    if (['prepend', 'append'].includes(this.selectedTransformation)) {
      const vars = this.transformation.variables;
      // For tables backward compatibility, use `prepend` or `append` if `substring` is not set
      this.substring = vars.substring || vars.prepend || vars.append;
    }

    if (["custom-script"].includes(this.selectedTransformation)) {
      this.customScript = this.transformation.variables.script;
    }
    if (["add-line-break-after-character"].includes(this.selectedTransformation)) {
      this.lineBreakCharacter = this.transformation.variables.lineBreakCharacter;
      this.addLineBreakIfWidthExceeded = !!this.transformation.variables.addLineBreakIfWidthExceeded;
    }    
    
    if (["map-field-based-on-characters-count"].includes(this.selectedTransformation)) {
      this.mapFieldBasedOnCharsCountGroupID = this.transformation.variables.mapFieldBasedOnCharsCountGroupID;
    }

    if (["columns-multiplier", "multiplier"].includes(this.selectedTransformation)) {
      // Handle both variable names for backward compatibility
      this.headersToMultiply = this.transformation.variables.headersToMultiply || this.transformation.variables.multiplierField || [];
    }

    if (["values-to-header"].includes(this.selectedTransformation)) {
      this.valueFieldToAdd = this.transformation.variables.valueFieldToAdd || '';
    }

    if (['split-field-by-space'].includes(this.selectedTransformation)) {
      this.splitBySpaceFields = this.transformation.variables.splitBySpaceFields;
    }

    if (['split-to-rows'].includes(this.selectedTransformation)) {
      this.splitRowsColsCount = this.transformation.variables.colsCount || 2;
      this.splitRowsAlignBulletsHorizontally = !!this.transformation.variables.isAlignHorizontal;
      const sortDirection = this.transformation.variables.sortByCharacters || false;
      this.splitRowsSortByNumberOfCharacters = ['asc', 'desc'].includes(sortDirection)
        ? sortDirection
        : false;
      this.splitRowsMoveBulletsToNextColumnIfHeightExceeded = this.transformation.variables.moveBulletsToNextColumnIfHeightExceeded || null;
    }

    if (['conditional-values'].includes(this.selectedTransformation)) {
      this.selectedColumn = this.transformation.variables.selectedColumn;
      this.selectedTableColumn = this.transformation.variables.selectedTableColumn ?? 'all';
      this.selectedCondition = this.transformation.variables.selectedCondition || '=';
      this.conditionValue = this.transformation.variables.conditionValue || '';
      this.newValue = this.transformation.variables.newValue || '';
    }

    if (['merge-table-cells-with-same-value'].includes(this.selectedTransformation)) {
      this.selectedTableColumn = this.tableColumnIndex === null
        ? null
        : Number(this.tableColumnIndex);
    }

    if (["columns-concatenation", "concatenate"].includes(this.selectedTransformation)) {
      this.concatenationSequence = this.buildConcatenationSequence(
        this.transformation.variables.concatenationSequence,
        this.transformation.variables.headersToConcatenate || [],
        this.transformation.variables.headersDelimiter || ''
      );
    }

    if (["format"].includes(this.selectedTransformation)) {
      this.formatField = this.transformation.variables.formatField || null;
      this.thousandSeparator = this.transformation.variables.thousandSeparator || ',';
    }

    if (['replace-product-table-empty-values'].includes(this.selectedTransformation)) {
      this.replaceEmptyValuesText = this.transformation.variables.replaceEmptyValuesText;
      this.replaceEmptyValuesField = this.transformation.variables.replaceEmptyValuesField;
    }

    if (['replace-product-table-fractions-with-glyph'].includes(this.selectedTransformation)) {
      this.replaceFractionsWithGlyphField = this.transformation.variables.replaceFractionsWithGlyphField;
    }
    
    if (['empty-when-related-field-is-empty'].includes(this.selectedTransformation)) {
      this.emptyRelatedFieldName = this.transformation.variables.emptyRelatedFieldName;
    }

    if (['rename-headers'].includes(this.selectedTransformation)) {
      this.initializeHeaderRenames();
    }

    // Load disableForEmpty and emptyText for all transformations
    if (this.transformation.variables.disableForEmpty !== undefined) {
      this.disableForEmpty = !!this.transformation.variables.disableForEmpty;
    } else {
      // Default to true (disabled) if not set
      this.disableForEmpty = true;
    }
    this.emptyText = this.transformation.variables.emptyText || '';
  },
  methods: {
    getTransformationData() {
      const transformation = {
        transformation: this.selectedTransformation,
        variables: {},
      };

      if (['shorten-product-description'].includes(transformation.transformation)) {
        transformation.variables.outputWords = this.outputWords;
      }

      if (['html-list-to-comma-separated-values'].includes(transformation.transformation)) {
        transformation.variables.maximumNumberOfValuesEnabled = this.maximumNumberOfValuesEnabled;
        transformation.variables.maximumNumberOfValues = this.maximumNumberOfValuesEnabled ? this.maximumNumberOfValues : 0;
        transformation.variables.delimiter = this.htmlListDelimiter || ',';
      }

      if (['add-line-break-after-character'].includes(transformation.transformation)) {
        transformation.variables.lineBreakCharacter = this.lineBreakCharacter;
        transformation.variables.addLineBreakIfWidthExceeded = this.addLineBreakIfWidthExceeded ? 1 : 0;
      }

      if (['ignore-html-tags'].includes(transformation.transformation)) {
        transformation.variables.ignoreLineBreaks = this.ignoreLineBreaks ? 1 : 0;
      }

      if (['crop-to-image'].includes(transformation.transformation)) {
        transformation.variables.cropPercentage = this.normalizeCropPercentage(this.cropPercentage);
      }

      if (['download-image-from-box'].includes(transformation.transformation) && this.boxFolderId !== '') {
        transformation.variables.folderId = this.boxFolderId;
      }

      if (['rotate-to-wider-side'].includes(transformation.transformation) && this.rotateConditionField) {
        transformation.variables.rotateConditionField = this.rotateConditionField;
        transformation.variables.rotateConditionValue = this.rotateConditionValue;
      }

      if (['map-field-based-on-characters-count'].includes(transformation.transformation)) {
        transformation.variables.mapFieldBasedOnCharsCountGroupID = this.mapFieldBasedOnCharsCountGroupID;
      }

      if (['multiple-images'].includes(transformation.transformation)) { // @todo
        // let field = $("#field_mapping_"+templateFieldIndex);
        // let field_name = field.attr("name");
        // if (field_name && field_name.indexOf('_') !== -1){
        // alert ("Please select this transformation on the first image field of the group")
        // return;
        // }
        // settings.variables.delimiter = $('[data-variable-name="delimiter"]').val();
        // toggle_image_fields(templateFieldIndex,containerQuery, true);
        if (this.delimiterCharacter) {
          transformation.variables.delimiter = this.delimiterCharacter;
        }
      } else if (['values-to-bullets', 'truncate'].includes(transformation.transformation)) {
        transformation.variables.delimiter = this.delimiterCharacter;
      } else if (['values-to-configurable-bullets', 'bullet-to-text'].includes(transformation.transformation)) {
        let bullet = $('[data-variable-name="configurableBullets"]').val();
        let bulletColor = $('[data-variable-name="configurableBulletsColor"]').val();

        if (this.delimiterCharacter) {
          transformation.variables.delimiter = this.delimiterCharacter;
        }

        // there's no need to set bullet if the field is empty
        if (this.bulletsToAdd) {
          transformation.variables.bullet = this.bulletsToAdd;
        }

        // custom bullet user input
        if (this.bulletsToAdd === 'custom_text') {
          transformation.variables.bullet = this.bulletsCustom;
        }

        //set color in case if the user pick another color
        //default color = #000001 if color = #000001 the bullet color will be as text
        if (this.bulletsToAdd && this.bulletColor !== '#000001') {
          transformation.variables.bullet_color = this.bulletColor;
        }

        if (this.addLineJumpIsVisible) {
          transformation.variables.add_line_jump = this.addLineJump ? 1 : 0;
        }

        if (this.sortValuesByCharacterCount) {
          transformation.variables.sort_values_by_character_count = this.sortValuesByCharacterCountParam ? 1 : 0;
          if (this.sortValuesByCharacterCountParam) {
            transformation.variables.sort_values_by_character_count_direction = this.sortValuesByCharacterCountDirection;
          }
        }

        transformation.variables.add_subsequent_lines_indentation = this.addSubsequentIndentation ? 1 : 0;

        if (transformation.transformation === 'values-to-configurable-bullets') {
          const itemsMargin = parseFloat(this.itemsMargin);
          transformation.variables.items_margin = Number.isFinite(itemsMargin) && itemsMargin > 0 ? itemsMargin : 0;
        }
      } else if (['columns-multiplier'].includes(transformation.transformation)) {
        transformation.variables.headersToMultiply = this.headersToMultiply;
      } else if (['values-to-header'].includes(transformation.transformation)) {
        transformation.variables.valueFieldToAdd = this.valueFieldToAdd;        
      } else if (['multiplier'].includes(transformation.transformation)) {
        // For table columns, use multiplierField variable name
        transformation.variables.multiplierField = this.headersToMultiply;
      } else if (['split-field-by-space'].includes(transformation.transformation)) {
        transformation.variables.splitBySpaceFields = this.splitBySpaceFields;
      } else if (['split-to-rows'].includes(transformation.transformation)) {
        const colsCount = parseInt(this.splitRowsColsCount, 10);
        transformation.variables.colsCount = Number.isFinite(colsCount) && colsCount > 1 ? colsCount : 2;
        transformation.variables.isAlignHorizontal = this.splitRowsAlignBulletsHorizontally ? 1 : 0;
        if (['asc', 'desc'].includes(this.splitRowsSortByNumberOfCharacters)) {
          transformation.variables.sortByCharacters = this.splitRowsSortByNumberOfCharacters;
        }

        const columnHeightLimit = parseFloat(this.splitRowsMoveBulletsToNextColumnIfHeightExceeded);
        if (Number.isFinite(columnHeightLimit) && columnHeightLimit > 0) {
          transformation.variables.moveBulletsToNextColumnIfHeightExceeded = columnHeightLimit;
        }
      } else if (['columns-concatenation', 'concatenate'].includes(transformation.transformation)) {
        transformation.variables.concatenationSequence = this.getNormalizedConcatenationSequence();
      } else if (['format'].includes(transformation.transformation)) {
        transformation.variables.formatField = this.formatField;
        if (this.formatField === 'thousand_separator') {
          transformation.variables.thousandSeparator = this.thousandSeparator;
        }
      } else if (['rename-headers'].includes(transformation.transformation)) {
        const renames = {};
        const allowedHeaders = new Set(this.renameHeadersHeaders);
        Object.entries(this.headerRenames || {}).forEach(([from, to]) => {
          if (!allowedHeaders.has(from)) {
            return;
          }
          const next = to === null || to === undefined ? '' : String(to).trim();
          if (next !== '') {
            renames[from] = next;
          }
        });
        transformation.variables.renames = renames;
      } else if (['prepend', 'append'].includes(transformation.transformation)) {
        transformation.variables.substring = this.substring;
      } else if (['custom-script', 'append'].includes(transformation.transformation)) {
        transformation.variables.script = this.customScript;
      } else { // @todo
        // // check if it previously had 'multiple-images' so we can re-enable the other inputs.
        // let previous_value_input = $(`${containerQuery} .field-mapping-transformation[data-template-field-index="${templateFieldIndex}"]`);
        // let previous_value = previous_value_input.val();
        // if (previous_value && JSON.parse(previous_value).transformation !== undefined && JSON.parse(previous_value).transformation == 'multiple-images' ){
        // toggle_image_fields(templateFieldIndex,containerQuery, false);
        // }
      }

      if (['replace-product-table-empty-values'].includes(transformation.transformation)) {
        transformation.variables.replaceEmptyValuesText = this.replaceEmptyValuesText;
        transformation.variables.replaceEmptyValuesField = this.replaceEmptyValuesField;
      }

      if (['replace-product-table-fractions-with-glyph'].includes(transformation.transformation)) {
        transformation.variables.replaceFractionsWithGlyphField = this.replaceFractionsWithGlyphField;
      }
      
      if (['empty-when-related-field-is-empty'].includes(transformation.transformation)) {
        transformation.variables.emptyRelatedFieldName = this.emptyRelatedFieldName;
      }

      if (['conditional-values'].includes(transformation.transformation)) {
        transformation.variables.selectedColumn = this.selectedColumn;
        transformation.variables.selectedCondition = this.selectedCondition;
        transformation.variables.conditionValue = this.conditionValue;
        transformation.variables.newValue = this.newValue;
        if (this.conditionalValuesUsesTableColumns) {
          transformation.variables.selectedTableColumn = this.selectedTableColumn;
        }
      }

      if (['merge-table-cells-with-same-value'].includes(transformation.transformation)) {
        transformation.variables.selectedTableColumn = this.tableColumnIndex === null
          ? null
          : Number(this.tableColumnIndex);
      }

      // Save disableForEmpty and emptyText for available transformations
      if (this.disableForEmptyAvailable.includes(this.selectedTransformation)) {
        transformation.variables.disableForEmpty = this.disableForEmpty ? 1 : 0;
        transformation.variables.emptyText = this.emptyText || '';
      }

      return transformation;
    },
    buildConcatenationSequence(sequence = [], legacyHeaders = [], legacyDelimiter = '') {
      if (Array.isArray(sequence) && sequence.length) {
        return sequence
          .map((item) => ({
            type: item.type === 'string' ? 'string' : 'field',
            value: item.value ?? item.header ?? item.field ?? null,
          }))
          .filter((item) => item.value !== null);
      }

      if (!legacyHeaders || !legacyHeaders.length) {
        return [];
      }

      const builtSequence = [];
      legacyHeaders.forEach((header, index) => {
        builtSequence.push({ type: 'field', value: header });
        if (legacyDelimiter !== '' && index < legacyHeaders.length - 1) {
          builtSequence.push({ type: 'string', value: legacyDelimiter });
        }
      });

      return builtSequence;
    },
    getNormalizedConcatenationSequence() {
      return this.concatenationSequence
        .map((item) => ({
          type: item.type === 'string' ? 'string' : 'field',
          value: item.value,
        }))
        .filter((item) => item.value !== null && item.value !== undefined);
    },
    addConcatenationField() {
      if (!this.concatenationFieldToAdd) {
        return;
      }

      this.concatenationSequence.push({
        type: 'field',
        value: this.concatenationFieldToAdd,
      });
      this.concatenationFieldToAdd = null;
    },

    addConcatenationString() {
      this.concatenationSequence.push({
        type: 'string',
        value: '',
      });
    },
    normalizeCropPercentage(value) {
      const cropPercentage = parseInt(value, 10);

      if (!Number.isFinite(cropPercentage)) {
        return 10;
      }

      return Math.min(100, Math.max(0, cropPercentage));
    },
    removeConcatenationItem(index) {
      this.concatenationSequence.splice(index, 1);
    },
    initializeHeaderRenames() {
      const existingRenames = this.transformation?.variables?.renames || {};
      if (existingRenames && typeof existingRenames === 'object' && !Array.isArray(existingRenames)) {
        this.headerRenames = { ...existingRenames };
      } else {
        this.headerRenames = {};
      }
    },
    setHeaderRename(header, value) {
      this.$set(this.headerRenames, header, value);
    },
    initializeCodeMirror() {
      const state = EditorState.create({
        doc: this.customScript,
        extensions: [
          basicSetup,
          javascript(),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              this.customScript = update.state.doc.toString();
            }
          }),
        ],
      });

      if (this.codeEditor) {
        this.codeEditor.destroy();
      }

      this.codeEditor = new EditorView({
        state,
        parent: this.$refs.customScriptCodeEditor
      });
    },
    testScript() {
      if (!this.customScript) {
        alert('Please enter a script or choose a saved script to test');
        return;
      }

      transformationsEventBroker.fire(TRANSFORMATIONS_EVENTS.TEST_SCRIPT, {
        script: this.customScript,
        instance: this,
      });
    },
    saveScript() {
      if (!this.customScript) {
        alert('Please enter a script to save');
        return;
      }

      const name = this.transformationScript
        ? this.scripts.find((script) => script.id === this.transformationScript).name
        : '';

      transformationsEventBroker.fire(TRANSFORMATIONS_EVENTS.SAVE_SCRIPT, {
        id: this.transformationScript,
        script: this.customScript,
        originalName: name,
        instance: this,
      });
    },
    setScript(script) {
      this.transformationScript = script;
    },
    deleteScript() {
      transformationsEventBroker.fire(TRANSFORMATIONS_EVENTS.DELETE_SCRIPT, {
        script: this.transformationScript,
        instance: this,
      });
    },
    hideTestOutput() {
      if (this.$refs.customScriptOutput) {
        this.$refs.customScriptOutput.innerHTML = '';
      }

      this.customScriptOutputVisible = false;
    },
    showTestOutput({output}) {
      this.customScriptOutputVisible = true;
      this.$refs.customScriptOutput.innerHTML = output;
    },
    setBullets() {
      //if bullet in select options set option, if not set as custom
      const recursiveFind = (nodes, callback) => {
        for (const node of nodes) {
          if (node.options) {
            const found = recursiveFind(node.options, callback);

            if (found) return found;
          }

          if (callback(node)) return node;
        }

        return undefined;
      };

      const bulletIsFromOptions = recursiveFind(
        this.bulletsOptions,
        (option) => option.value === this.transformation.variables.bullet,
      );

      if (bulletIsFromOptions) {
        this.bulletsToAdd = this.transformation.variables.bullet;
      } else {
        this.bulletsToAdd = 'custom_text';
        this.bulletsCustom = this.transformation.variables.bullet;
      }

      //set custom color or get default
      this.bulletColor = this.transformation.variables.bullet_color || '#000001';
    }
  },
}
</script>

<style scoped>
.conditional-values-if-col {
  min-width: 250px;
}

.conditional-values-if-col ::v-deep .multiselect__content-wrapper {
  width: auto;
  min-width: 100%;
  max-width: 450px;
}
</style>


